import { useCallback, useEffect, useState } from "react";
import { api, type ConnectorEnrollment, type ConnectorInfo, type Organization } from "../api/client";
import { Icons } from "./icons";
import { useT } from "../i18n";

// Admin screen for outbound connectors (jump server Phase 2): per-org list with live status, create
// (→ one-time encrypted bootstrap bundle), and revoke. Reuses the org-chip selector pattern from the
// Connect screen. Admin-only (mounted behind the admin nav).
export function ConnectorsAdmin({
  orgs,
  selectedOrg,
  openOrg,
}: {
  orgs: Organization[];
  selectedOrg: Organization | null;
  openOrg: (org: Organization) => void;
}) {
  const t = useT();
  const [connectors, setConnectors] = useState<ConnectorInfo[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [enrollment, setEnrollment] = useState<ConnectorEnrollment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orgId = selectedOrg?.id;
  const refresh = useCallback(async () => {
    if (!orgId) return;
    try {
      const { connectors } = await api.listConnectors(orgId);
      setConnectors(connectors);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [orgId]);

  // Poll status while the screen is open (monitoring). refresh() only setStates after an await, so
  // the cascading-render rule is a false positive here.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
    const id = window.setInterval(refresh, 15000);
    return () => window.clearInterval(id);
  }, [refresh]);

  async function create() {
    if (!orgId || !name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const enr = await api.createConnector(orgId, name.trim());
      setEnrollment(enr);
      setName("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (!window.confirm(t("Revoke this connector? Its tunnel will stop working."))) return;
    try {
      await api.revokeConnector(id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">
      <div className="mx-auto w-full max-w-4xl">
        <header className="mb-5">
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Icons.rdp className="h-5 w-5 text-accent" />
            {t("Connectors")}
          </h1>
          <p className="mt-1 text-sm text-muted">
            {t("Outbound edge agents that let the jump server reach a customer's internal targets without inbound firewall rules.")}
          </p>
        </header>

        {orgs.length > 0 && (
          <div className="mb-5 flex flex-wrap gap-2">
            {orgs.map((org) => (
              <button
                key={org.id}
                onClick={() => openOrg(org)}
                className={`rounded-full border px-3 py-1 text-sm ${
                  selectedOrg?.id === org.id ? "border-accent bg-accent/10 text-fg" : "border-border text-muted hover:border-accent"
                }`}
              >
                {org.is_personal ? t("Personal") : org.name}
              </button>
            ))}
          </div>
        )}

        {error && <div className="mb-4 rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">{error}</div>}

        {!selectedOrg ? (
          <p className="mt-10 text-center text-sm text-muted">{t("Select an organization.")}</p>
        ) : (
          <>
            <div className="mb-5 flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("New connector name (e.g. Acme HQ)")}
                className="flex-1 rounded border border-border bg-panel px-3 py-1.5 text-sm"
              />
              <button
                onClick={create}
                disabled={busy || !name.trim()}
                className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2 disabled:opacity-50"
              >
                {t("Create")}
              </button>
            </div>

            <div className="flex flex-col gap-2">
              {connectors.length === 0 && <p className="text-sm text-muted">{t("No connectors yet.")}</p>}
              {connectors.map((c) => (
                <div key={c.id} className="flex items-center gap-3 rounded-lg border border-border bg-panel p-3">
                  <StatusDot status={c.status} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{c.name}</div>
                    <div className="truncate font-mono text-xs text-muted">
                      {c.status}
                      {c.version ? ` · v${c.version}` : ""}
                      {c.last_seen ? ` · ${t("seen")} ${new Date(c.last_seen).toLocaleString()}` : ""}
                    </div>
                  </div>
                  {c.status !== "revoked" && (
                    <button
                      onClick={() => revoke(c.id)}
                      className="rounded border border-border px-2 py-1 text-xs text-muted hover:border-danger hover:text-danger"
                    >
                      {t("Revoke")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {enrollment && <EnrollmentDialog enr={enrollment} onClose={() => setEnrollment(null)} />}
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: ConnectorInfo["status"] }) {
  const color =
    status === "online" ? "bg-ok" : status === "revoked" ? "bg-danger" : status === "offline" ? "bg-warn" : "bg-muted";
  return <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${color}`} />;
}

// Decrypt the AES-GCM bundle (nonce(12)||ciphertext, key = SHA-256(password)) in the browser and let
// the operator download the plaintext ZIP. Shown once — the material is not retrievable again.
function EnrollmentDialog({ enr, onClose }: { enr: ConnectorEnrollment; onClose: () => void }) {
  const t = useT();
  const [downloading, setDownloading] = useState(false);

  async function download() {
    if (!enr.bundle_password || !enr.bundle_zip_b64) return;
    setDownloading(true);
    try {
      const raw = Uint8Array.from(atob(enr.bundle_zip_b64), (ch) => ch.charCodeAt(0));
      const nonce = raw.slice(0, 12);
      const ct = raw.slice(12);
      const keyBytes = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(enr.bundle_password));
      const key = await crypto.subtle.importKey("raw", keyBytes, "AES-GCM", false, ["decrypt"]);
      const zip = await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce }, key, ct);
      const url = URL.createObjectURL(new Blob([zip], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${enr.cn}-bootstrap.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-lg border border-border bg-panel p-5">
        <h2 className="mb-2 text-base font-semibold">{t("Connector created")}</h2>
        <p className="mb-3 text-sm text-muted">
          {t("Download the bootstrap bundle and run install.sh on the customer's edge host. This is shown only once.")}
        </p>
        <div className="mb-3 rounded border border-border bg-panel2 p-2 font-mono text-xs">
          <div className="truncate">CN: {enr.cn}</div>
          <div className="truncate">{t("Tunnel")}: {enr.tunnel_host || "—"}</div>
        </div>
        <div className="flex gap-2">
          {enr.bundle_zip_b64 ? (
            <button
              onClick={download}
              disabled={downloading}
              className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent2 disabled:opacity-50"
            >
              {t("Download bootstrap.zip")}
            </button>
          ) : (
            <span className="text-xs text-muted">{t("Bundle unavailable (server not fully configured).")}</span>
          )}
          <button onClick={onClose} className="ml-auto rounded border border-border px-3 py-1.5 text-sm hover:border-accent">
            {t("Done")}
          </button>
        </div>
      </div>
    </div>
  );
}
